
export const translations = {
  en: {
    nav: {
      dashboard: 'Dashboard',
      myOrders: 'My Orders',
      invoices: 'Financials',
      priceList: 'Price List',
      workbench: 'Workbench',
      queue: 'Queue',
      allOrders: 'All Orders',
      users: 'Team & Clients',
      financials: 'Financials',
      settings: 'Settings',
      logout: 'Sign Out',
      support: 'Support',
      myClinic: 'My Clinic',
      overview: 'Overview',
      designerRates: 'Designer Rates',
      servicesCatalog: 'Services Catalog',
      pricePlans: 'Price Plans',
      millingCenters: 'Milling Centers',
      topUpRequests: 'Top-Up Requests'
    },
    landing: {
      // --- HERO SECTION ---
      badge: 'System v4.0 Live',
      heroTitle: 'The First Dental Lab <br /> with an Online Platform.',
      heroDesc: "Exclusively for dentists. Manage orders, track financials, and deliver perfection. <br /> An operating system designed for the world's best clinicians.",
      getStarted: 'Start for free',
      login: 'Login',
      watchShowreel: 'Watch Showreel',
      navFeatures: 'Features',
      navShowcase: 'Showcase',
      navWorkflow: 'Workflow',

      // --- FEATURES GRID ---
      features: {
        dashboard: {
          title: 'Centralized Command Center',
          subtitle: 'A unified dashboard to view every case, patient, and invoice in real-time.'
        },
        financial: {
          title: 'Financial Intelligence',
          subtitle: 'Track clinic expenses, case costs, and invoices with zero friction.'
        },
        collaboration: {
          title: 'Seamless Collaboration',
          subtitle: 'Connect directly with technicians in one fluid workflow.'
        },
        workflow: {
          title: 'End-to-End Workflow',
          subtitle: 'From STL upload to delivery dispatch, every step is automated.'
        }
      },

      // --- SHOWCASE SECTION ---
      showcase: {
        title: 'Craftsmanship <br /> <span class="text-zinc-500">Meet Technology.</span>',
        description: "Showcase your lab's finest work. High-resolution galleries allow dentists to see the quality of your prosthetics before they even place an order.",
        checks: {
          viewer: '3D STL Viewer Integration',
          comparison: 'Before & After Comparisons',
          portfolios: 'Technician Portfolios'
        },
        viewGallery: 'View Full Gallery',
        cases: [
          { title: 'Full Mouth Rehabilitation', type: 'Zirconia & Titanium' },
          { title: 'Anterior Aesthetics', type: 'E.max Veneers' },
          { title: 'Smile Design', type: 'Digital Planning' },
          { title: 'Implant Restoration', type: 'Custom Abutments' },
          { title: 'Ceramic Veneers', type: 'Hand-Layered' },
          { title: 'Zirconia Crowns', type: 'Monolithic Strength' },
          { title: 'All-on-4', type: 'Implant Bridge' },
          { title: 'Posterior Monolithic', type: 'High Translucency' }
        ]
      },

      // --- INTEGRATION MARQUEE ---
      integrations: {
        title: 'Seamlessly Integrated With'
      },

      // --- FAQ SECTION ---
      faq: {
        title: 'Frequently Asked Questions',
        items: [
          {
            q: "Do I need an intraoral scanner?",
            a: "While preferred, it's not strictly required. However, Galal Dental Lab is a digital-first lab. We can digitize physical impressions, but the workflow is optimized for direct STL/PLY uploads from scanners like 3Shape, Medit, or iTero."
          },
          {
            q: "How does the wallet system work?",
            a: "It operates like a prepaid balance or credit line. You top up your account via bank transfer or Instapay. The cost of each case is deducted automatically upon dispatch. This ensures zero billing delays."
          },
          {
            q: "Can I communicate with the technician directly?",
            a: "Yes. Every case has a dedicated chat channel. You can send voice notes, photos, or text instructions directly to the designer working on your case."
          }
        ]
      },

      // --- CTA FOOTER ---
      footer: {
        readyTitle: 'Ready to upgrade?',
        getStartedNow: 'Get Started Now',
        copyright: '© 2024 Galal Lab. Designed in Cairo.',
        privacy: 'Privacy Policy',
        terms: 'Terms of Service'
      }
    },
    dashboard: {
      welcome: 'Welcome back',
      newCase: 'New Case',
      inProduction: 'In Production',
      completed: 'Completed',
      outstanding: 'Outstanding',
      searchPlaceholder: 'Search patient, order ID...',
      noOrders: 'No orders found',
      activeCases: 'Active Cases',
      pendingPayments: 'Pending Payments',
      newClients: 'New Clients',
      implantTracker: 'Implant & Abutment Tracker',
      implantTrackerDesc: 'Real-time monitoring of all implant cases.',
      searchImplant: 'Search abutment type, tooth...',
      noImplantCases: 'No recent implant cases found.',
      designerMatrix: 'Designer Rates Matrix',
      designerMatrixDesc: 'Set specific rates for each technician per service.',
      balance: 'Balance',
      topUp: 'Top Up Balance',
      history: 'History',
      recentActivity: 'Recent Activity',
      viewAll: 'View All Orders',
      quickActions: 'Quick Actions',
      topUpWallet: 'Top Up Wallet',
      topUpDesc: 'Add funds securely',
      checkPayments: 'Check payments',
      viewPrices: 'View services & prices',
      tableHeaders: {
        id: 'Order ID',
        patient: 'Patient',
        type: 'Type',
        tooth: 'Tooth',
        status: 'Status',
        due: 'Due Date',
        actions: 'Actions',
        date: 'Date',
        doctor: 'Doctor',
        service: 'Service',
        details: 'Abutment / Details',
        serviceName: 'Service Name',
        basePrice: 'Base Price'
      }
    },
    common: {
      loading: 'Loading...',
      details: 'Details',
      submit: 'Submit',
      cancel: 'Cancel',
      upload: 'Upload',
      view3D: 'View 3D Model',
      download: 'Download',
      confirmAction: 'Are you sure you want to proceed?',
      darkMode: 'Dark Mode',
      lightMode: 'Light Mode',
      switchLang: 'Switch to Arabic',
      offline: 'You are offline. Reconnecting...',
      staff: 'Staff / Mod'
    },
    admin: {
      totalRevenue: 'Total Revenue',
      activeJobs: 'Active Jobs',
      avgTurnaround: 'Avg Turnaround',
      needsAttention: 'Needs Attention',
      usersList: 'Registered Users',
      addUser: 'Add User'
    }
  },
  ar: {
    nav: {
      dashboard: 'لوحة التحكم',
      myOrders: 'حالاتي',
      invoices: 'الحسابات',
      priceList: 'قائمة الأسعار',
      workbench: 'منصة الشغل',
      queue: 'قائمة الانتظار',
      allOrders: 'كل الطلبات',
      users: 'العملاء والفريق',
      financials: 'التقارير المالية',
      settings: 'إعدادات الحساب',
      logout: 'خروج',
      support: 'الدعم الفني',
      myClinic: 'عيادتي',
      overview: 'نظرة عامة',
      designerRates: 'أسعار المصممين',
      servicesCatalog: 'دليل الخدمات',
      pricePlans: 'خطط الأسعار',
      millingCenters: 'مراكز الخرط',
      topUpRequests: 'طلبات الشحن'
    },
    landing: {
      // --- HERO SECTION ---
      badge: 'المنظومة رقم ٤.٠ تعمل الآن',
      heroTitle: 'أول معمل أسنان <br /> بمنصة إلكترونية.',
      heroDesc: "حصرياً لأطباء الأسنان. تابع الطلبات، راقب الحسابات، وقدم أفضل جودة. <br /> نظام تشغيل صُمم خصيصاً لأفضل الأطباء في العالم.",
      getStarted: 'ابدأ مجاناً',
      login: 'دخول',
      watchShowreel: 'شاهد الفيديو',
      navFeatures: 'المميزات',
      navShowcase: 'أعمالنا',
      navWorkflow: 'نظام الشغل',

      // --- FEATURES GRID ---
      features: {
        dashboard: {
          title: 'مركز التحكم الموحد',
          subtitle: 'لوحة تحكم شاملة لمتابعة كل حالة، مريض، وفاتورة في الوقت الفعلي.'
        },
        financial: {
          title: 'ذكاء مالي',
          subtitle: 'تتبع مصاريف العيادة، تكاليف الحالات، والفواتير بسهولة تامة.'
        },
        collaboration: {
          title: 'تعاون بلا حدود',
          subtitle: 'تواصل مباشرة مع الفنيين في سير عمل واحد سلس.'
        },
        workflow: {
          title: 'سير عمل متكامل',
          subtitle: 'من رفع ملفات STL وحتى تسليم الشحنة، كل خطوة تتم بشكل آلي.'
        }
      },

      // --- SHOWCASE SECTION ---
      showcase: {
        title: 'الحرفة اليدوية <br /> <span class="text-zinc-500">تلتقي بالتكنولوجيا.</span>',
        description: "اعرض أفضل أعمال معملك. معارض صور عالية الدقة تسمح للأطباء برؤية جودة التركيبات قبل حتى الطلب.",
        checks: {
          viewer: 'عارض ملفات STL ثلاثي الأبعاد',
          comparison: 'مقارنات قبل وبعد',
          portfolios: 'ملفات أعمال الفنيين'
        },
        viewGallery: 'عرض المعرض الكامل',
        cases: [
          { title: 'إعادة تأهيل الفم بالكامل', type: 'زيركونيا وتيتانيوم' },
          { title: 'تجميل الأسنان الأمامية', type: 'فينير E.max' },
          { title: 'تصميم الابتسامة', type: 'تخطيط رقمي' },
          { title: 'تركيبات الزراعة', type: 'دعامات مخصصة' },
          { title: 'فينير سيراميك', type: 'بناء يدوي' },
          { title: 'تيجان زيركونيا', type: 'قوة مونوليثية' },
          { title: 'All-on-4', type: 'كوبري زراعة' },
          { title: 'أسنان خلفية', type: 'شفافية عالية' }
        ]
      },

      // --- INTEGRATION MARQUEE ---
      integrations: {
        title: 'متكامل بسلاسة مع'
      },

      // --- FAQ SECTION ---
      faq: {
        title: 'أسئلة شائعة',
        items: [
          {
            q: "هل أحتاج إلى ماسح ضوئي داخل الفم (Intraoral Scanner)؟",
            a: "على الرغم من أنه مفضل، إلا أنه ليس إلزامياً. لكن معمل جلال هو معمل رقمي بالدرجة الأولى. يمكننا تحويل المقاسات التقليدية إلى رقمية، ولكن سير العمل مصمم ليكون أفضل مع ملفات STL/PLY المباشرة من أجهزة مثل 3Shape أو Medit أو iTero."
          },
          {
            q: "كيف يعمل نظام المحفظة؟",
            a: "يعمل مثل الرصيد مسبق الدفع. تقوم بشحن حسابك عن طريق التحويل البنكي أو Instapay. يتم خصم تكلفة كل حالة تلقائياً عند الشحن. هذا يضمن عدم وجود أي تأخير في الفواتير."
          },
          {
            q: "هل يمكنني التواصل مع الفني المسؤول مباشرة؟",
            a: "نعم. كل حالة لها قناة محادثة مخصصة. يمكنك إرسال ملاحظات صوتية، صور، أو نصوص مباشرة للمصمم الذي يعمل على حالتك."
          }
        ]
      },

      // --- CTA FOOTER ---
      footer: {
        readyTitle: 'جاهز للتطوير؟',
        getStartedNow: 'ابدأ الآن',
        copyright: '© ٢٠٢٤ معمل جلال. صمم في القاهرة.',
        privacy: 'سياسة الخصوصية',
        terms: 'الشروط والأحكام'
      }
    },
    dashboard: {
      welcome: 'أهلاً بك يا دكتور',
      newCase: 'طلب جديد',
      inProduction: 'تحت التنفيذ',
      completed: 'مكتملة',
      outstanding: 'الرصيد المستحق',
      searchPlaceholder: 'بحث باسم المريض أو رقم الطلب...',
      noOrders: 'مفيش طلبات حالياً',
      activeCases: 'حالات نشطة',
      pendingPayments: 'مدفوعات معلقة',
      newClients: 'عملاء جدد',
      implantTracker: 'تتبع زراعة الأسنان',
      implantTrackerDesc: 'متابعة حية لكل حالات الزراعة.',
      searchImplant: 'بحث بنوع الدعامة، السن...',
      noImplantCases: 'لا توجد حالات زراعة حديثة.',
      designerMatrix: 'لائحة أسعار المصممين',
      designerMatrixDesc: 'تحديد أسعار خاصة لكل فني لكل خدمة.',
      balance: 'الرصيد',
      topUp: 'شحن الرصيد',
      history: 'السجل',
      recentActivity: 'النشاط الأخير',
      viewAll: 'عرض كل الطلبات',
      quickActions: 'إجراءات سريعة',
      topUpWallet: 'شحن المحفظة',
      topUpDesc: 'أضف رصيد بأمان',
      checkPayments: 'راجع المدفوعات',
      viewPrices: 'عرض الأسعار والخدمات',
      tableHeaders: {
        id: 'رقم الطلب',
        patient: 'اسم المريض',
        type: 'نوع التركيبة',
        tooth: 'السن',
        status: 'الحالة',
        due: 'التسليم',
        actions: 'إجراءات',
        date: 'التاريخ',
        doctor: 'الدكتور',
        service: 'الخدمة',
        details: 'تفاصيل / دعامة',
        serviceName: 'اسم الخدمة',
        basePrice: 'السعر الأساسي'
      }
    },
    common: {
      loading: 'جاري التحميل...',
      details: 'التفاصيل',
      submit: 'إرسال الطلب',
      cancel: 'إلغاء',
      upload: 'رفع الملفات',
      view3D: 'معاينة 3D',
      download: 'تحميل',
      confirmAction: 'هل أنت متأكد من الاستمرار؟',
      darkMode: 'الوضع الليلي',
      lightMode: 'الوضع النهاري',
      switchLang: 'Switch to English',
      offline: 'لا يوجد اتصال بالإنترنت. جاري إعادة الاتصال...',
      staff: 'مسؤول / مشرف'
    },
    admin: {
      totalRevenue: 'إجمالي الدخل',
      activeJobs: 'حالات نشطة',
      avgTurnaround: 'سرعة الإنجاز',
      needsAttention: 'تنبيهات',
      usersList: 'قائمة المستخدمين',
      addUser: 'إضافة عضو'
    }
  }
};

export type Language = 'en' | 'ar';
export const Direction = {
  en: 'ltr',
  ar: 'rtl'
};
